import { useState, useEffect } from 'react';

interface DominantColorResult {
  hex: string;           // e.g. "#D4832A"
  rgb: [number, number, number];
  rgba: (alpha: number) => string;
  isDark: boolean;       // whether the color itself is dark (for contrast decisions)
}

const DEFAULT: DominantColorResult = {
  hex: '#FFD54F',
  rgb: [255, 213, 79],
  rgba: (a) => `rgba(255,213,79,${a})`,
  isDark: false,
};

/**
 * Loads an image into an off-screen canvas, samples a grid of pixels,
 * and returns the single most-saturated vivid dominant color.
 *
 * Uses k-means-lite (3 clusters) to find the dominant palette,
 * then picks the cluster with the highest saturation × population score
 * so it favours bold, vivid hues over near-white/near-black areas.
 */
export function useDominantColor(src: string): DominantColorResult {
  const [result, setResult] = useState<DominantColorResult>(DEFAULT);

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const SIZE = 80; // downsample to 80×80 for speed
        const canvas = document.createElement('canvas');
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const { data } = ctx.getImageData(0, 0, SIZE, SIZE);

        // Collect pixels, skip very dark (<30 brightness) and very light (>230)
        const pixels: [number, number, number][] = [];
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 128) continue;
          const brightness = (r + g + b) / 3;
          if (brightness < 30 || brightness > 230) continue;
          pixels.push([r, g, b]);
        }

        if (pixels.length === 0) return;

        // k-means with k=5 clusters, 8 iterations
        const k = 5;
        let centroids = pixels
          .filter((_, idx) => idx % Math.floor(pixels.length / k) === 0)
          .slice(0, k);

        for (let iter = 0; iter < 8; iter++) {
          const clusters: [number, number, number][][] = Array.from({ length: k }, () => []);

          for (const p of pixels) {
            let best = 0, bestDist = Infinity;
            for (let c = 0; c < k; c++) {
              const dr = p[0] - centroids[c][0];
              const dg = p[1] - centroids[c][1];
              const db = p[2] - centroids[c][2];
              const d = dr * dr + dg * dg + db * db;
              if (d < bestDist) { bestDist = d; best = c; }
            }
            clusters[best].push(p);
          }

          centroids = clusters.map((cl, ci) => {
            if (cl.length === 0) return centroids[ci];
            const sum = cl.reduce(([ar, ag, ab], [r, g, b]) => [ar + r, ag + g, ab + b], [0, 0, 0]);
            return [sum[0] / cl.length, sum[1] / cl.length, sum[2] / cl.length] as [number, number, number];
          });
        }

        // Score each centroid: saturation * sqrt(population)
        const clusterSizes: number[] = Array(k).fill(0);
        for (const p of pixels) {
          let best = 0, bestDist = Infinity;
          for (let c = 0; c < k; c++) {
            const dr = p[0] - centroids[c][0];
            const dg = p[1] - centroids[c][1];
            const db = p[2] - centroids[c][2];
            const d = dr * dr + dg * dg + db * db;
            if (d < bestDist) { bestDist = d; best = c; }
          }
          clusterSizes[best]++;
        }

        let bestScore = -1, bestIdx = 0;
        for (let c = 0; c < k; c++) {
          const [r, g, b] = centroids[c];
          const max = Math.max(r, g, b) / 255;
          const min = Math.min(r, g, b) / 255;
          const saturation = max === 0 ? 0 : (max - min) / max;
          const score = saturation * Math.sqrt(clusterSizes[c]);
          if (score > bestScore) { bestScore = score; bestIdx = c; }
        }

        const [r, g, b] = centroids[bestIdx].map(Math.round) as [number, number, number];
        const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        setResult({
          hex,
          rgb: [r, g, b],
          rgba: (a) => `rgba(${r},${g},${b},${a})`,
          isDark: brightness < 128,
        });
      } catch {
        // canvas tainted or other error — keep default
      }
    };

    img.onerror = () => { /* keep default */ };
    img.src = src;
  }, [src]);

  return result;
}
